import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useProfilePictureUpload } from "@/hooks/use-profile-picture-upload";
import { SquareCropDialog } from "@/components/square-crop-dialog";
import {
  Camera,
  Check,
  ChevronRight,
  Loader2,
  Mail,
  CalendarDays,
  Globe,
  Bell,
  User,
  Phone,
  ArrowLeft,
} from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { cn } from "@/lib/utils";

interface UserSetupWizardProps {
  profile: {
    role?: string;
    onboardingStep?: number | null;
    onboardingCompletedAt?: string | null;
    gmailConnectedEmail?: string | null;
    bridgePhone?: string | null;
  };
}

interface AuthUser {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  profileImageUrl?: string | null;
}

interface CalendarStatus {
  connected: boolean;
  email?: string | null;
  meetConnected?: boolean;
}

interface NotificationPref {
  inApp: boolean;
  email: boolean;
}

const STEPS = ["You", "Connect", "Preferences", "Profile"];

const COMMON_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Puerto_Rico", label: "Atlantic Time (AT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Central European (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "Europe/Helsinki", label: "Eastern European (EET)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Bangkok", label: "Indochina (ICT)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Tokyo", label: "Japan (JST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
  { value: "Pacific/Auckland", label: "New Zealand (NZST)" },
];

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Chicago";
  }
}

function getInitials(first?: string | null, last?: string | null): string {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}

export function UserSetupWizard({ profile }: UserSetupWizardProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const initialStep = Math.max(1, Math.min(profile.onboardingStep ?? 1, 4));
  const [step, setStep] = useState(initialStep);

  const { data: authUser } = useQuery<AuthUser>({ queryKey: ["/api/auth/user"] });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState(profile.bridgePhone ?? "");

  useEffect(() => {
    if (authUser) {
      setFirstName((prev) => prev || authUser.firstName || "");
      setLastName((prev) => prev || authUser.lastName || "");
    }
  }, [authUser]);

  const [timezone, setTimezone] = useState(detectTimezone);
  const [notifPrefs, setNotifPrefs] = useState<Record<string, NotificationPref>>({
    "lead.assigned": { inApp: true, email: true },
    "invoice.paid": { inApp: true, email: true },
    "event.booked": { inApp: true, email: false },
    "lead.new": { inApp: true, email: false },
  });

  const [bio, setBio] = useState("");
  const [title, setTitle] = useState("");

  const [savingStep, setSavingStep] = useState(false);

  const { data: calStatus } = useQuery<CalendarStatus>({
    queryKey: ["/api/user/calendar/status"],
    refetchOnWindowFocus: true,
  });

  const { data: meProfile } = useQuery<{ profile: { bioShort?: string | null; title?: string | null; profilePicture?: string | null } | null }>({
    queryKey: ["/api/profiles/me"],
  });

  useEffect(() => {
    if (meProfile?.profile) {
      setBio(meProfile.profile.bioShort ?? "");
      setTitle(meProfile.profile.title ?? "");
    }
  }, [meProfile]);

  const gmailConnected = !!profile.gmailConnectedEmail;
  const calConnected = !!calStatus?.connected;

  const uploadHook = useProfilePictureUpload({
    onUploaded: () => {
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
      qc.invalidateQueries({ queryKey: ["/api/profile-real"] });
    },
  });

  const avatarUrl = meProfile?.profile?.profilePicture ?? undefined;

  const advanceStep = useCallback(async (targetStep: number) => {
    await apiRequest("PATCH", "/api/profile/onboarding", { onboardingStep: targetStep });
  }, []);

  const markComplete = useCallback(async () => {
    await apiRequest("PATCH", "/api/profile/onboarding", { completed: true });
    qc.invalidateQueries({ queryKey: ["/api/profile"] });
    qc.invalidateQueries({ queryKey: ["/api/profile-real"] });
  }, [qc]);

  const handleStep1Next = async () => {
    setSavingStep(true);
    try {
      if (firstName.trim() || lastName.trim()) {
        await apiRequest("PATCH", "/api/profile", {
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        });
      }
      if (phone.trim() !== (profile.bridgePhone ?? "")) {
        await apiRequest("PATCH", "/api/settings/dialer", {
          bridgePhone: phone.trim() || null,
        });
      }
      await advanceStep(2);
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
      qc.invalidateQueries({ queryKey: ["/api/profile-real"] });
      setStep(2);
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message, variant: "destructive" });
    } finally {
      setSavingStep(false);
    }
  };

  const handleStep2Next = async () => {
    setSavingStep(true);
    try {
      await advanceStep(3);
      setStep(3);
    } finally {
      setSavingStep(false);
    }
  };

  const handleStep3Next = async () => {
    setSavingStep(true);
    try {
      const prefsPayload: Record<string, { inApp: boolean; email: boolean; push: boolean }> = {};
      for (const [cat, val] of Object.entries(notifPrefs)) {
        prefsPayload[cat] = { inApp: val.inApp, email: val.email, push: false };
      }
      await apiRequest("PUT", "/api/notification-preferences", {
        preferences: prefsPayload,
        workingHours: {
          enabled: false,
          start: "09:00",
          end: "17:00",
          timezone,
          days: [1, 2, 3, 4, 5],
        },
      });
      await advanceStep(4);
      setStep(4);
    } catch (e: any) {
      toast({ title: "Couldn't save preferences", description: e?.message, variant: "destructive" });
    } finally {
      setSavingStep(false);
    }
  };

  const handleFinish = async (skip = false) => {
    setSavingStep(true);
    try {
      if (!skip && (bio.trim() || title.trim())) {
        await apiRequest("PATCH", "/api/profiles/me", {
          bioShort: bio.trim() || undefined,
          title: title.trim() || undefined,
        });
      }
      await markComplete();
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message, variant: "destructive" });
      setSavingStep(false);
    }
  };

  const handleGmailConnect = async () => {
    await apiRequest("PATCH", "/api/profile/onboarding", { onboardingStep: 2 });
    // Both /api/gmail/auth and /api/user/calendar/auth return { authUrl } as
    // JSON, not a redirect.  A bare window.location.href would render raw JSON
    // when the server returns an error (e.g. EMAIL_NOT_VERIFIED → 403), so we
    // fetch the URL and navigate only on success.
    try {
      const res = await fetch("/api/gmail/auth?returnUrl=/", { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast({ title: "Gmail Connection Failed", description: body.message || "Unable to connect Gmail. Please try again.", variant: "destructive" });
        return;
      }
      const data = await res.json();
      if (data.authUrl) window.location.href = data.authUrl;
    } catch (e: any) {
      toast({ title: "Gmail Connection Failed", description: e?.message || "Network error. Please try again.", variant: "destructive" });
    }
  };

  const handleCalendarConnect = async () => {
    await apiRequest("PATCH", "/api/profile/onboarding", { onboardingStep: 2 });
    // Same fetch-based pattern — avoids raw JSON on error in the browser.
    try {
      const res = await fetch("/api/user/calendar/auth?meet=1", { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast({ title: "Calendar Connection Failed", description: body.message || "Unable to connect Calendar. Please try again.", variant: "destructive" });
        return;
      }
      const data = await res.json();
      if (data.authUrl) window.location.href = data.authUrl;
    } catch (e: any) {
      toast({ title: "Calendar Connection Failed", description: e?.message || "Network error. Please try again.", variant: "destructive" });
    }
  };

  const toggleNotif = (cat: string, channel: "inApp" | "email") => {
    setNotifPrefs((prev) => ({
      ...prev,
      [cat]: {
        ...prev[cat],
        [channel]: !prev[cat]?.[channel],
      },
    }));
  };

  return (
    <>
      <SquareCropDialog
        open={uploadHook.cropOpen}
        file={uploadHook.selectedFile}
        onCancel={uploadHook.cancelCrop}
        onConfirm={uploadHook.confirmCrop}
        isUploading={uploadHook.isUploading}
      />

      <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden max-h-[90vh]">
          <div className="px-8 pt-8 pb-6 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
                Step {step} of {STEPS.length}
              </span>
              <div className="flex gap-1.5">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-300",
                      i + 1 < step
                        ? "w-4 bg-primary"
                        : i + 1 === step
                        ? "w-6 bg-primary"
                        : "w-4 bg-muted"
                    )}
                  />
                ))}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-foreground mt-4">
              {step === 1 && "Tell us about you"}
              {step === 2 && "Connect your accounts"}
              {step === 3 && "Set your preferences"}
              {step === 4 && "Round out your profile"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {step === 1 && "This is how teammates and clients will see you."}
              {step === 2 && "Connect Gmail, Calendar, and Google Meet to hit the ground running."}
              {step === 3 && "Choose your timezone and which alerts to receive."}
              {step === 4 && "Add a short bio and title — or skip for now."}
            </p>
          </div>

          <div className="overflow-y-auto flex-1 px-8">
            {step === 1 && (
              <Step1You
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                email={authUser?.email ?? ""}
                phone={phone}
                setPhone={setPhone}
                avatarUrl={avatarUrl}
                initials={getInitials(firstName, lastName)}
                uploadHook={uploadHook}
              />
            )}
            {step === 2 && (
              <Step2Connect
                gmailConnected={gmailConnected}
                gmailEmail={profile.gmailConnectedEmail}
                calConnected={calConnected}
                calEmail={calStatus?.email}
                onConnectGmail={handleGmailConnect}
                onConnectCalendar={handleCalendarConnect}
              />
            )}
            {step === 3 && (
              <Step3Preferences
                timezone={timezone}
                setTimezone={setTimezone}
                notifPrefs={notifPrefs}
                toggleNotif={toggleNotif}
              />
            )}
            {step === 4 && (
              <Step4Profile
                bio={bio}
                setBio={setBio}
                title={title}
                setTitle={setTitle}
              />
            )}
          </div>

          <div className="px-8 py-6 flex-shrink-0 border-t border-border bg-card">
            <div className="flex items-center justify-between gap-3">
              {step > 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep((s) => s - 1)}
                  disabled={savingStep}
                  data-testid="button-wizard-back"
                  className="gap-1.5"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              <div className="flex gap-2">
                {step === 4 && (
                  <Button
                    variant="outline"
                    onClick={() => handleFinish(true)}
                    disabled={savingStep}
                    data-testid="button-wizard-skip"
                  >
                    Skip for now
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (step === 1) handleStep1Next();
                    else if (step === 2) handleStep2Next();
                    else if (step === 3) handleStep3Next();
                    else handleFinish(false);
                  }}
                  disabled={savingStep}
                  data-testid={`button-wizard-step${step}-next`}
                  className="gap-2 min-w-[110px]"
                >
                  {savingStep ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : step === 4 ? (
                    <>
                      Finish
                      <Check className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Step1You({
  firstName, setFirstName,
  lastName, setLastName,
  email,
  phone, setPhone,
  avatarUrl,
  initials,
  uploadHook,
}: {
  firstName: string; setFirstName: (v: string) => void;
  lastName: string; setLastName: (v: string) => void;
  email: string;
  phone: string; setPhone: (v: string) => void;
  avatarUrl?: string;
  initials: string;
  uploadHook: ReturnType<typeof useProfilePictureUpload>;
}) {
  return (
    <div className="space-y-5 pb-2">
      <div className="flex items-center gap-5">
        <div className="relative flex-shrink-0">
          <Avatar className="h-20 w-20 ring-2 ring-border">
            <AvatarImage src={avatarUrl} alt="Your photo" />
            <AvatarFallback className="text-xl bg-muted text-muted-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          <button
            type="button"
            onClick={uploadHook.pickFile}
            disabled={uploadHook.isUploading}
            data-testid="button-upload-photo"
            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors disabled:opacity-60"
            title="Upload photo"
          >
            {uploadHook.isUploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Camera className="h-3.5 w-3.5" />
            )}
          </button>
          <input {...uploadHook.fileInputProps} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Profile photo</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Optional — helps teammates recognize you.
          </p>
          {avatarUrl && (
            <button
              type="button"
              onClick={uploadHook.removePhoto}
              disabled={uploadHook.isRemoving}
              data-testid="button-remove-photo"
              className="text-xs text-muted-foreground hover:text-destructive transition-colors mt-1"
            >
              {uploadHook.isRemoving ? "Removing…" : "Remove photo"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="wizard-first-name" className="text-sm">
            First name
          </Label>
          <Input
            id="wizard-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First"
            data-testid="input-wizard-first-name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wizard-last-name" className="text-sm">
            Last name
          </Label>
          <Input
            id="wizard-last-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last"
            data-testid="input-wizard-last-name"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="wizard-email" className="text-sm">
          Email
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="wizard-email"
            value={email}
            readOnly
            className="pl-9 bg-muted/40 text-muted-foreground cursor-default"
            data-testid="input-wizard-email"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Your account email from sign-in — can't be changed here.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="wizard-phone" className="text-sm">
          Mobile number{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="wizard-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 000 0000"
            className="pl-9"
            data-testid="input-wizard-phone"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Used to ring your phone on incoming calls through the dialer.
        </p>
      </div>
    </div>
  );
}

function ConnectCard({
  icon,
  title,
  description,
  connected,
  connectedLabel,
  onConnect,
  testId,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  connected: boolean;
  connectedLabel?: string | null;
  onConnect: () => void;
  testId: string;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-4 p-4 rounded-xl border transition-colors",
        connected
          ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30"
          : "border-border bg-card hover:bg-accent/30"
      )}
    >
      <div
        className={cn(
          "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0",
          connected ? "bg-green-100 dark:bg-green-900/50" : "bg-muted"
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-foreground">{title}</span>
          {connected && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
              <Check className="h-3 w-3" />
              Connected
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {connected && connectedLabel ? connectedLabel : description}
        </p>
      </div>
      {!connected && (
        <Button
          size="sm"
          variant="outline"
          onClick={onConnect}
          data-testid={testId}
          className="flex-shrink-0 gap-1.5"
        >
          <SiGoogle className="h-3.5 w-3.5" />
          Connect
        </Button>
      )}
    </div>
  );
}

function Step2Connect({
  gmailConnected,
  gmailEmail,
  calConnected,
  calEmail,
  onConnectGmail,
  onConnectCalendar,
}: {
  gmailConnected: boolean;
  gmailEmail?: string | null;
  calConnected: boolean;
  calEmail?: string | null;
  onConnectGmail: () => void;
  onConnectCalendar: () => void;
}) {
  return (
    <div className="space-y-3 pb-2">
      <ConnectCard
        icon={<Mail className="h-5 w-5 text-muted-foreground" />}
        title="Gmail"
        description="Send and track emails directly from Clarik."
        connected={gmailConnected}
        connectedLabel={gmailEmail ?? undefined}
        onConnect={onConnectGmail}
        testId="button-connect-gmail"
      />
      <ConnectCard
        icon={<CalendarDays className="h-5 w-5 text-muted-foreground" />}
        title="Google Calendar & Meet"
        description="Sync events and create Meet links automatically."
        connected={calConnected}
        connectedLabel={calEmail ? `${calEmail} · Calendar + Meet` : undefined}
        onConnect={onConnectCalendar}
        testId="button-connect-calendar"
      />
      <p className="text-xs text-muted-foreground pt-1">
        Both are optional — you can connect them later in Settings.
      </p>
    </div>
  );
}

const NOTIF_CATEGORIES = [
  {
    key: "lead.assigned",
    label: "Lead assigned to me",
    description: "When a lead is assigned to you.",
    icon: <User className="h-4 w-4" />,
  },
  {
    key: "invoice.paid",
    label: "Invoice paid",
    description: "When a client pays an invoice.",
    icon: <Check className="h-4 w-4" />,
  },
  {
    key: "event.booked",
    label: "Event booked",
    description: "When a new event is confirmed on your schedule.",
    icon: <CalendarDays className="h-4 w-4" />,
  },
  {
    key: "lead.new",
    label: "New lead arrives",
    description: "When a fresh lead lands in the pipeline.",
    icon: <Bell className="h-4 w-4" />,
  },
];

function Step3Preferences({
  timezone,
  setTimezone,
  notifPrefs,
  toggleNotif,
}: {
  timezone: string;
  setTimezone: (v: string) => void;
  notifPrefs: Record<string, NotificationPref>;
  toggleNotif: (cat: string, channel: "inApp" | "email") => void;
}) {
  const detectedLabel = COMMON_TIMEZONES.find((t) => t.value === timezone)?.label;

  return (
    <div className="space-y-5 pb-2">
      <div className="space-y-1.5">
        <Label className="text-sm flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          Your timezone
        </Label>
        <Select value={timezone} onValueChange={setTimezone}>
          <SelectTrigger data-testid="select-timezone">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {COMMON_TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!detectedLabel && (
          <p className="text-xs text-muted-foreground">
            Auto-detected as <span className="font-mono">{timezone}</span>.{" "}
            Pick the closest match above.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-sm flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          Notifications
        </Label>
        <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-4 py-2 bg-muted/40">
            <span className="text-xs font-medium text-muted-foreground">Alert</span>
            <span className="text-xs font-medium text-muted-foreground text-center w-10">In-app</span>
            <span className="text-xs font-medium text-muted-foreground text-center w-10">Email</span>
          </div>
          {NOTIF_CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-none">{cat.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
              </div>
              <div className="flex items-center justify-center w-10">
                <Switch
                  checked={notifPrefs[cat.key]?.inApp ?? true}
                  onCheckedChange={() => toggleNotif(cat.key, "inApp")}
                  data-testid={`switch-notif-inapp-${cat.key}`}
                  className="scale-90"
                />
              </div>
              <div className="flex items-center justify-center w-10">
                <Switch
                  checked={notifPrefs[cat.key]?.email ?? false}
                  onCheckedChange={() => toggleNotif(cat.key, "email")}
                  data-testid={`switch-notif-email-${cat.key}`}
                  className="scale-90"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step4Profile({
  bio, setBio,
  title, setTitle,
}: {
  bio: string; setBio: (v: string) => void;
  title: string; setTitle: (v: string) => void;
}) {
  return (
    <div className="space-y-5 pb-2">
      <div className="space-y-1.5">
        <Label htmlFor="wizard-title" className="text-sm">
          Job title{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id="wizard-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Senior Booking Agent"
          data-testid="input-wizard-title"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="wizard-bio" className="text-sm">
          Short bio{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Textarea
          id="wizard-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Tell teammates a little about yourself — your background, specialty, or what you love about the work."
          rows={4}
          maxLength={300}
          data-testid="textarea-wizard-bio"
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground text-right">
          {bio.length}/300
        </p>
      </div>
    </div>
  );
}
